import { errorLogger } from "@/Common/helpers/logger";
import { approveAllWashouts } from "@/Washouts/api/PayrollClosing/mutations";
import { useMutation } from "@tanstack/react-query";
import useApproveAllWashoutSubscription from "../common/useApproveAllWashoutSubscription";
import { useSnackbar } from "@/Common/hooks/Snackbar/useSnackbar";
import { generateAllWashouts, retractAllWashouts } from "@/Washouts/api/WashoutReview/mutations";

export interface IUseAllWashoutReviewActionsArgs {
	locationId: string;
	commissionCycleId: string;
	payPeriodId: string;
	currentUser: string;
}

export const useAllWashoutReviewActions = (args: IUseAllWashoutReviewActionsArgs) => {
	const { locationId, commissionCycleId, payPeriodId, currentUser } = args;

	const { showSnackBar } = useSnackbar();

	useApproveAllWashoutSubscription({ locationId, commissionCycleId, payPeriodId });

	const { mutate: approveAllWashoutsMutation, isPending: isPendingApproveAllWashoutMutation } = useMutation({
		mutationFn: async (input: { commissionCycleId: string; payPeriodId: string; positions: string[] }) => {
			approveAllWashouts({
				userId: currentUser,
				locationId,
				...input,
			});
		},
		onSuccess: () => {
			showSnackBar("Successfully approved all washouts");
		},
		onError: (err) => {
			showSnackBar(`Error approving all washouts: ${JSON.stringify(err)}`, true);
			errorLogger(err);
		},
	});

	const { mutate: retractAllWashoutsMutation, isPending: isPendingRetractAllWashoutMutation } = useMutation({
		mutationFn: async (input: { commissionCycleId: string; payPeriodId: string; positions: string[] }) =>
			retractAllWashouts({
				userId: currentUser,
				locationId,
				...input,
			}),
		onSuccess: () => {
			showSnackBar("Successfully retracted all washouts");
		},
		onError: (err) => {
			showSnackBar(`Error retracting all washouts: ${JSON.stringify(err)}`, true);
			errorLogger(err);
		},
	});

	const { mutate: generateAllWashoutsMutation, isPending: isPendingGenerateAllWashoutMutation } = useMutation({
		mutationFn: async (input: { commissionCycleId: string; payPeriodId: string; positions: string[] }) =>
			generateAllWashouts({
				locationId,
				...input,
			}),
		onSuccess: () => {
			showSnackBar("Successfully generated all washouts");
		},
		onError: (err) => {
			showSnackBar(`Error generating all washouts: ${JSON.stringify(err)}`, true);
			errorLogger(err);
		},
	});

	return {
		approveAllWashoutsMutation,
		retractAllWashoutsMutation,
		generateAllWashoutsMutation,
		isPendingApproveAllWashoutMutation,
		isPendingRetractAllWashoutMutation,
		isPendingGenerateAllWashoutMutation,
	};
};
